import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, IconButton, Typography, Box, TextField, InputAdornment, Avatar, MenuItem, Select, CircularProgress, styled, Switch, BaseTextFieldProps } from '@mui/material';
import Grid from '@mui/material/Grid2';
import moment from 'moment';
import { CountryIso2, defaultCountries, FlagImage, parseCountry, usePhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { toast } from 'react-toastify';

// Import local components and services with proper paths
import { EditWithUnderscroreIcon, ModalClose } from '../../assets/icons';
import { SignupRequest } from '../../types/auth';
import { SubscriptionData } from '../../types/chargebee';
import AuthService from '../../services/auth.service';
import { decodeMetadataToken } from '../../utils/token.utils';
import ConfirmCardPayment from './ConfirmCardPayment';
import ChargebeeService from '../../services/chargebee.service';
import PricingSection from '../../components/subscription/pricing/PricingSection';
import { formatPlanName } from '../../utils/common.util';
import PricingSectionV2 from '../../components/subscription/pricing/PricingSectionV2';
import { config } from '../../config';

interface AddWorkspaceProps {
  open: boolean;
  onClose: () => void;
  title: string;
  content: string;
  onConfirm: () => void;
}

interface CustomDropdownProps {
  title: string;
  options: string[];
  defaultValue: string;
  onSelect: (selected: string) => void;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  title,
  options,
  defaultValue,
  onSelect,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(defaultValue);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleOptionClick = (value: string) => {
    setSelectedValue(value);
    onSelect(value); // Notify parent of the selection
    setIsOpen(false); // Close the dropdown after selection
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // Handle toggling dropdowns and close all other dropdowns
  useEffect(() => {
    if (isOpen) {
      const closeOtherDropdowns = () => {
        document.querySelectorAll('.custom-dropdown.open').forEach((dropdown) => {
          if (dropdown !== dropdownRef.current) {
            dropdown.classList.remove('open');
          }
        });
      };
      closeOtherDropdowns();
    }
  }, [isOpen]);

  return (
    <div className={`custom-dropdown dropdown ${isOpen ? "open" : ""}`} ref={dropdownRef}>
      <div className="dropdown-header" onClick={toggleDropdown}>
        <div>
          <span className="title">{title}</span>
          <span className="value">{selectedValue}</span>
        </div>
        <span className={`chevron ${isOpen ? "rotate" : ""}`}></span>
      </div>
      {isOpen && (
        <ul className="dropdown-options">
          {options.map((option, index) => (
            <li
              key={index}
              className={option === selectedValue ? "selected" : ""}
              onClick={() => handleOptionClick(option)}
            >
              {option}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export interface MUIPhoneProps extends BaseTextFieldProps {
  value: string;
  onChange: (phone: string) => void;
}

export const MuiPhone: React.FC<MUIPhoneProps> = ({
  value,
  onChange,
  ...restProps
}) => {
  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } =
    usePhoneInput({
      defaultCountry: 'us',
      value,
      countries: defaultCountries,
      onChange: (data) => {
        onChange(data.phone);
      },
      disableCountryGuess: true,
      forceDialCode: true,
    });

  return (
    <TextField
      variant="outlined"
      color="primary"
      placeholder="Phone number"
      value={inputValue}
      onChange={handlePhoneValueChange}
      type="tel"
      inputRef={inputRef}
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: '8px',
          borderColor: '#666666',
        },

        " .MuiInputLabel-root.Mui-focused": {
          color: "inherit",
        },

        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: '#333333',
          border: '1px solid'
        },

        "& .MuiOutlinedInput-input": {
          padding: '14px 16px',
          background: '#ffffff',
          borderRadius: '8px'
        },
        "&.MuiInputBase-input::placeholder": {
          color: '#333333',
          fontSize: '16px',
          opacity: '0.8'
        },
      }}
      slotProps={{
        input: {
          startAdornment: <InputAdornment
            position="start"
            style={{ marginRight: '2px', marginLeft: '-8px' }}
          >
            <Select
              MenuProps={{
                style: {
                  height: '300px',
                  width: '360px',
                  top: '10px',
                  left: '-34px',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
              }}
              sx={{
                width: 'max-content',
                fieldset: {
                  display: 'none',
                },
                '&.Mui-focused:has(div[aria-expanded="false"])': {
                  fieldset: {
                    display: 'block',
                  },
                },
                '.MuiSelect-select': {
                  padding: '8px',
                  paddingRight: '24px !important',
                },
                svg: {
                  right: 0,
                },
              }}
              value={country.iso2}
              onChange={(e) => setCountry(e.target.value as CountryIso2)}
              renderValue={(value) => (
                <FlagImage iso2={value} style={{ display: 'flex' }} />
              )}
            >
              {defaultCountries.map((c) => {
                const country = parseCountry(c);
                return (
                  <MenuItem key={country.iso2} value={country.iso2}>
                    <FlagImage
                      iso2={country.iso2}
                      style={{ marginRight: '8px' }}
                    />
                    <Typography marginRight="8px">{country.name}</Typography>
                    <Typography color="gray">+{country.dialCode}</Typography>
                  </MenuItem>
                );
              })}
            </Select>
          </InputAdornment>
        },
      }}
      {...restProps}
    />
  );
};

interface Tier {
  starting_unit: number;
  ending_unit?: number;
  price: number;
}

const AddWorkspace: React.FC<AddWorkspaceProps> = ({ open, onClose, onConfirm }) => {
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [currentStep, setCurrentStep] = useState<'details' | 'plans'>('details');
  const [selectedPlan, setSelectedPlan] = useState<string>('');

  // for confirmation card payment modal popup
  const [PaymentModal, setPaymentModal] = useState(false);
  const [selectedPlanDetails, setSelectedPlanDetails] = useState({
    planName: '',
    units: 0,
    amount: 0,
    frequency: ''
  });

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const AntSwitch = styled(Switch)(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    display: 'flex',
    '&:active': {
      '& .MuiSwitch-thumb': {
        width: 15,
      },
      '& .MuiSwitch-switchBase.Mui-checked': {
        transform: 'translateX(18px)',
        top: "2px"
      },
    },
    '& .MuiSwitch-switchBase': {
      padding: 2,
      top: "2px",
      left: "2px",
      '&.Mui-checked': {
        transform: 'translateX(17px)',
        color: '#fff',
        top: "2px",
        '& + .MuiSwitch-track': {
          opacity: 1,
          backgroundColor: '#178EFC',
        },
      },
    },
    '& .MuiSwitch-thumb': {
      boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
      width: 18,
      height: 18,
      borderRadius: 18,
      transition: "200ms ease all",
    },
    '& .MuiSwitch-track': {
      borderRadius: 18,
      opacity: 1,
      backgroundColor: '#178EFC',
      boxSizing: 'border-box',
    },
  }));

  const handleNext = () => {
    if (currentStep === 'details') {
      if (validateForm()) {
        setCurrentStep('plans');
      }
    }
  };

  // Add state for profile photo
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string>('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log("File select triggered");
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      console.log("File selected:", file.name, file.type, file.size);

      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size too large. Please select an image under 5MB');
        return;
      }

      setProfilePhoto(file);

      // Create a preview URL
      const objectUrl = URL.createObjectURL(file);
      console.log("Created object URL:", objectUrl);
      setProfilePhotoUrl(objectUrl);

      // Force a re-render by using a setTimeout
      setTimeout(() => {
        console.log("Current profilePhotoUrl state:", profilePhotoUrl);
      }, 100);
    }
  };

  // Trigger file input click
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Upload profile photo
  const uploadProfilePhoto = async (): Promise<string | null> => {
    if (!profilePhoto) return null;

    try {
      setUploadingPhoto(true);
      const formData = new FormData();
      formData.append('file', profilePhoto);

      const response = await AuthService.uploadProfilePhoto(formData);
      return response.data.url;
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload profile photo');
      return null;
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePlanSelect = async (couponCode?: string) => {
    try {
      setError('');
      setIsLoading(true);

      // Process the full name into first/last name
      const nameParts = formData.fullName.trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const metadata = decodeMetadataToken();

      // Upload profile photo if selected
      let photoUrl = null;
      if (profilePhoto) {
        photoUrl = await uploadProfilePhoto();
      }

      // Prepare signup request with selected plan and coupon code
      const signupRequest: SignupRequest = {
        email: formData.email,
        password: formData.password,
        firstName: firstName,
        lastName: lastName,
        companyName: formData.companyName,
        jobTitle: formData.jobTitle || undefined,
        phoneNumber: phone,
        timezone: getTimezoneString(),
        parentCompanyId: metadata?.companies?.[0]?.id,
        planId: selectedPlan,
        planUnits: selectedPlanDetails.units,
        profileImage: photoUrl || undefined,
        couponId: couponCode
      };

      const response = await AuthService.signup(signupRequest);
      toast.success('Account created successfully!');
      onConfirm();

    } catch (err: any) {
      const errorMessage = err.message || 'Failed to create account';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Add form state
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    companyName: '',
    jobTitle: '',
    password: '',
    confirmPassword: '',
  });

  // Add error state
  const [error, setError] = useState('');

  // Add loading state
  const [isLoading, setIsLoading] = useState(false);

  // Add form validation state
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for the changed field
    setFormErrors(prev => ({
      ...prev,
      [name]: ''
    }));

    // Special validation for password and confirm password
    if (name === 'password') {
      const passwordError = validatePassword(value);
      if (!passwordError && formData.confirmPassword) {
        // Clear confirm password error if passwords match
        if (value === formData.confirmPassword) {
          setFormErrors(prev => ({
            ...prev,
            confirmPassword: ''
          }));
        }
      }
    }

    if (name === 'confirmPassword') {
      if (value === formData.password) {
        setFormErrors(prev => ({
          ...prev,
          confirmPassword: ''
        }));
      }
    }
  };

  const getTimezoneString = () => {
    const timezone = moment.tz.guess() || 'UTC';
    const offset = moment.tz(timezone).format('Z');
    const zoneName = moment.tz(timezone).zoneName();
    return `(GMT${offset}),${timezone},${zoneName}`;
  };

  // Validate form fields
  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.fullName.trim()) {
      errors.fullName = 'Full name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email';
    }

    if (!formData.companyName.trim()) {
      errors.companyName = 'Company name is required';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (!phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!validatePhone(phone)) {
      errors.phone = 'Please enter a valid phone number';
    }

    // Password validation
    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      errors.password = passwordError;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const [phone, setPhone] = useState('');
  const [phoneBlurred, setPhoneBlurred] = useState(false);

  const validatePassword = (password: string) => {
    const minLength = 8;
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      return 'Password must be at least 8 characters long';
    }
    if (!hasNumber) {
      return 'Password must contain at least one number';
    }
    if (!hasSpecialChar) {
      return 'Password must contain at least one special character';
    }
    return '';
  };

  const validatePhone = (phoneNumber: string) => {
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    return digitsOnly.length >= 10 && digitsOnly.length <= 15;
  };

  const handlePhoneChange = (value: string) => {
    setPhone(value);
    if (phoneBlurred) {
      if (validatePhone(value)) {
        setFormErrors(prev => ({
          ...prev,
          phone: ''
        }));
      }
    }
  };

  const handlePhoneBlur = () => {
    setPhoneBlurred(true);
    if (phone.trim()) {
      const isValid = validatePhone(phone);
      if (!isValid) {
        setFormErrors(prev => ({
          ...prev,
          phone: 'Please enter a valid phone number'
        }));
      }
    } else {
      setFormErrors(prev => ({
        ...prev,
        phone: 'Phone number is required'
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      fullName: '',
      email: '',
      companyName: '',
      jobTitle: '',
      password: '',
      confirmPassword: '',
    });
    setPhone('');
    setFormErrors({});
    setError('');
    setPhoneBlurred(false);
    setShowPassword(false);
    setShowConfirmPassword(false);

    // Clear profile photo state
    setProfilePhoto(null);
    if (profilePhotoUrl) {
      URL.revokeObjectURL(profilePhotoUrl);
      setProfilePhotoUrl('');
    }
  };

  const handleClose = () => {
    resetForm();
    setCurrentStep('details'); // Reset to first step
    onClose(); // Call the parent's onClose handler
  };

  const handleConfirmationClose = () => {
    setPaymentModal(false) // Call the parent's onClose handler
  };

  const [plans, setPlans] = useState<any[]>([]);
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData[] | null>(null);

  // Helper function to determine if sale should be shown based on subscription conditions
  const shouldShowSale = useMemo(() => {
    if (!subscriptionData || subscriptionData.length === 0) return false;
    
    // Get the first subscription (or you could find a specific one based on your logic)
    const currentSubscription = subscriptionData[0];
    
    return config.isSaleLive && (
      currentSubscription?.subscription?.status === "in_trial" ||
      currentSubscription?.subscription_items?.some((item: any) => item.itemPriceId?.includes("free-trial-plan")) ||
      currentSubscription?.subscription?.billingPeriodUnit === "month"
    );
  }, [subscriptionData]);

  // Function to get pricing options from plans
  const getPricingOptions = () => {
    const automatedGoldMonthly = plans.find(
      (p) => p.id === "Automated-Gold-USD-Monthly"
    );

    // Get unique attendee tiers
    const attendeeOptions =
      automatedGoldMonthly?.item_price.tiers
        .filter((tier: Tier) => tier.ending_unit)
        .map((tier: Tier) => tier.ending_unit!.toString()) || [];

    return {
      attendeeOptions,
      getPrice: (planId: string, attendees: number) => {
        const plan = plans.find((p) => p.id === planId);
        const tier = plan?.item_price.tiers.find(
          (t: Tier) =>
            attendees >= t.starting_unit &&
            (!t.ending_unit || attendees <= t.ending_unit)
        );
        return tier ? tier.price / 100 : 0; // Convert cents to dollars
      },
    };
  };

  const { attendeeOptions, getPrice } = getPricingOptions();


  const getSelectedPlanId = (planId: string) => {
    // Extract base plan ID (e.g., "Automated-Gold-USD-Monthly" from "Automated-Gold-USD-Monthly-200")
    const basePlanId = planId.split('-').slice(0, -1).join('-');

    // Extract units from the last part (e.g., "200" from "Automated-Gold-USD-Monthly-200")
    const units = parseInt(planId.split('-').pop() || '0');

    // Extract plan name (e.g., "Automated-Gold" from "Automated-Gold-USD-Monthly")
    const planName = formatPlanName(basePlanId);

    setSelectedPlan(basePlanId);

    // Calculate amount based on plan type and units
    const amount = getPrice(basePlanId, units);

    //Get Frequency
    const frequency = basePlanId.includes('Monthly') ? 'Month' : 'Year';

    // Set selected plan details
    setSelectedPlanDetails({
      planName: planName,
      units: units,
      amount: amount,
      frequency: frequency
    });

    setPaymentModal(true);
  };

  useEffect(() => {
    const fetchPlansData = async () => {
      try {
        const plansResult = await ChargebeeService.getPlans();
        setPlans(plansResult.data || []);
      } catch (error) {
        toast.error("Failed to fetch plans");
      }
    };

    const fetchSubscriptionData = async () => {
      try {
        const metadata = decodeMetadataToken();
        const customerId = metadata?.companies?.[0]?.id;
        if (customerId) {
          const response = await ChargebeeService.getSubscriptionDetails(customerId);
          setSubscriptionData(response.data);
        }
      } catch (error) {
        // Subscription not found or other error - this is expected for new workspaces
        console.log("No subscription data found for new workspace");
      }
    };

    Promise.all([fetchPlansData(), fetchSubscriptionData()]);
  }, []);

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      if (profilePhotoUrl) {
        URL.revokeObjectURL(profilePhotoUrl);
      }
    };
  }, [profilePhotoUrl]);

  const renderDialogContent = () => {
    if (currentStep === 'details') {
      return (
        <DialogContent dividers sx={{ borderTop: 'none', borderBottom: 'none', p: { xs: "16px", sm: "25px 32px 32px" } }}>
          <Grid container rowSpacing={{ xs: 3, sm: 3, md: 3 }} columnSpacing={{ xs: 3, sm: 2, md: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }} display="flex" justifyContent={{ xs: "center", sm: "start" }}>
              <Box position="relative" borderRadius="100%" width="102px" height="102px">
                {/* Hidden file input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  accept="image/*"
                  onChange={handleFileSelect}
                />

                {/* Profile photo container */}
                <Box
                  position="relative"
                  width="100%"
                  height="100%"
                  borderRadius="50%"
                  overflow="hidden"
                  onClick={handleUploadClick}
                  sx={{
                    cursor: "pointer",
                    '&:hover .edit-overlay': {
                      opacity: 1
                    }
                  }}
                >
                  {profilePhotoUrl ? (
                    // Show selected image
                    <>
                      <img
                        src={profilePhotoUrl}
                        alt="Selected profile"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block'
                        }}
                      />
                      {/* Edit overlay that appears on hover */}
                      <Box
                        className="edit-overlay"
                        position="absolute"
                        top={0}
                        left={0}
                        width="100%"
                        height="100%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                          backgroundColor: "rgba(0, 0, 0, 0.5)",
                          opacity: 0,
                          transition: "opacity 0.2s ease-in-out"
                        }}
                      >
                        <EditWithUnderscroreIcon />
                      </Box>
                    </>
                  ) : (
                    // Show default avatar
                    <Avatar
                      sx={{
                        width: '100%',
                        height: '100%',
                        bgcolor: '#E6E6E6'
                      }}
                    />
                  )}

                  {/* Upload overlay - only show when no photo is selected */}
                  {!profilePhotoUrl && (
                    <Box
                      position="absolute"
                      top={0}
                      left={0}
                      width="100%"
                      height="100%"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        border: "1px dashed #A3A3A3",
                        backgroundColor: "rgba(230, 230, 230, 0.7)",
                        cursor: "pointer"
                      }}
                    >
                      <Box textAlign="center">
                        <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/camera-add.svg" alt="camera_add_icon" />
                        <Typography variant="caption" color="#666666" display="block" fontWeight="500">Upload Photo</Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
              <Typography color='#333333' fontSize='14px' fontWeight='500' mb='12px' sx={{ opacity: '0.6' }}>
                Full Name <Box component="span" color="error.main">*</Box>
              </Typography>
              <TextField
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                variant="outlined"
                placeholder='Enter full name'
                required
                error={!!formErrors.fullName}
                helperText={formErrors.fullName}
                autoComplete="off"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: '8px',
                    borderColor: '#C7C7C7',
                  },

                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: '#333333',
                    border: '1px solid'
                  },

                  "& .MuiOutlinedInput-input": {
                    padding: '14px 16px',
                  },
                  "&.MuiInputBase-input::placeholder": {
                    color: '#333333',
                    fontSize: '16px',
                    opacity: '0.8'
                  },
                  // Add these styles to remove autofill color
                  "& input:-webkit-autofill": {
                    WebkitBoxShadow: "0 0 0 100px #ffffff inset",
                    WebkitTextFillColor: "#333333",
                  },
                  "& input:-webkit-autofill:focus": {
                    WebkitBoxShadow: "0 0 0 100px #ffffff inset",
                    WebkitTextFillColor: "#333333",
                  }
                }}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
              <Typography color='#333333' fontSize='14px' fontWeight='500' mb='12px' sx={{ opacity: '0.6' }}>
                Email <Box component="span" color="error.main">*</Box>
              </Typography>
              <TextField
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                variant="outlined"
                placeholder='Enter email'
                required
                error={!!formErrors.email}
                helperText={formErrors.email}
                autoComplete="off"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: '8px',
                    borderColor: '#C7C7C7',
                    background: "#ffffff"
                  },

                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: '#333333',
                    border: '1px solid'
                  },

                  "& .MuiOutlinedInput-input": {
                    padding: '14px 16px',
                    background: '#ffffff',
                    borderRadius: '8px'
                  },
                  "&.MuiInputBase-input::placeholder": {
                    color: '#333333',
                    fontSize: '16px',
                    opacity: '0.8'
                  },
                  // Add these styles to remove autofill color
                  "& input:-webkit-autofill": {
                    WebkitBoxShadow: "0 0 0 100px #ffffff inset",
                    WebkitTextFillColor: "#333333",
                  },
                  "& input:-webkit-autofill:focus": {
                    WebkitBoxShadow: "0 0 0 100px #ffffff inset",
                    WebkitTextFillColor: "#333333",
                  }
                }}
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17 20.5H7C4 20.5 2 19 2 15.5V8.5C2 5 4 3.5 7 3.5H17C20 3.5 22 5 22 8.5V15.5C22 19 20 20.5 17 20.5Z" stroke="#333333" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M17 9L13.87 11.5C12.84 12.32 11.15 12.32 10.12 11.5L7 9" stroke="#333333" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    </InputAdornment>,
                  },
                }}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
              <Typography color='#333333' fontSize='14px' fontWeight='500' mb='12px' sx={{ opacity: '0.6' }}>
                Company Name <Box component="span" color="error.main">*</Box>
              </Typography>
              <TextField
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                variant="outlined"
                placeholder='Enter company name'
                required
                error={!!formErrors.companyName}
                helperText={formErrors.companyName}
                autoComplete="off"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: '8px',
                    borderColor: '#C7C7C7',
                  },

                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: '#333333',
                    border: '1px solid'
                  },

                  "& .MuiOutlinedInput-input": {
                    padding: '14px 16px',
                  },
                  "&.MuiInputBase-input::placeholder": {
                    color: '#333333',
                    fontSize: '16px',
                    opacity: '0.8'
                  },
                  // Add these styles to remove autofill color
                  "& input:-webkit-autofill": {
                    WebkitBoxShadow: "0 0 0 100px #ffffff inset",
                    WebkitTextFillColor: "#333333",
                  },
                  "& input:-webkit-autofill:focus": {
                    WebkitBoxShadow: "0 0 0 100px #ffffff inset",
                    WebkitTextFillColor: "#333333",
                  }
                }}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
              <Typography color='#333333' fontSize='14px' fontWeight='500' mb='12px' sx={{ opacity: '0.6' }}>Job Title (Optional)</Typography>
              <TextField
                name="jobTitle"
                value={formData.jobTitle}
                onChange={handleInputChange}
                variant="outlined"
                placeholder='Enter job title'
                autoComplete="off"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: '8px',
                    borderColor: '#C7C7C7',
                  },

                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: '#333333',
                    border: '1px solid'
                  },

                  "& .MuiOutlinedInput-input": {
                    padding: '14px 16px',
                  },
                  "&.MuiInputBase-input::placeholder": {
                    color: '#333333',
                    fontSize: '16px',
                    opacity: '0.8'
                  },
                  // Add these styles to remove autofill color
                  "& input:-webkit-autofill": {
                    WebkitBoxShadow: "0 0 0 100px #ffffff inset",
                    WebkitTextFillColor: "#333333",
                  },
                  "& input:-webkit-autofill:focus": {
                    WebkitBoxShadow: "0 0 0 100px #ffffff inset",
                    WebkitTextFillColor: "#333333",
                  }
                }}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
              <Typography color='#333333' fontSize='14px' fontWeight='500' mb='12px' sx={{ opacity: '0.6' }}>
                Phone Number <Box component="span" color="error.main">*</Box>
              </Typography>
              <MuiPhone
                value={phone}
                onChange={handlePhoneChange}
                onBlur={handlePhoneBlur}
                error={!!formErrors.phone}
                helperText={formErrors.phone}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
              <Typography color='#333333' fontSize='14px' fontWeight='500' mb='12px' sx={{ opacity: '0.6' }}>
                Password <Box component="span" color="error.main">*</Box>
              </Typography>
              <TextField
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                variant="outlined"
                placeholder='Enter password'
                type={showPassword ? 'text' : 'password'}
                required
                error={!!formErrors.password}
                helperText={formErrors.password}
                autoComplete="new-password"
                sx={{
                  width: '100%',
                  "& .MuiOutlinedInput-root": {
                    borderRadius: '8px',
                    borderColor: '#C7C7C7',
                    background: '#ffffff',
                    paddingRight: '0'
                  },

                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: '#333333',
                    border: '1px solid'
                  },

                  "& .MuiOutlinedInput-input": {
                    padding: '14px 16px',
                    background: '#ffffff',
                    borderRadius: '8px'
                  },
                  "&.MuiInputBase-input::placeholder": {
                    color: '#333333',
                    fontSize: '16px',
                    opacity: '0.8'
                  },
                  // Add these styles to remove autofill color
                  "& input:-webkit-autofill": {
                    WebkitBoxShadow: "0 0 0 100px #ffffff inset",
                    WebkitTextFillColor: "#333333",
                  },
                  "& input:-webkit-autofill:focus": {
                    WebkitBoxShadow: "0 0 0 100px #ffffff inset",
                    WebkitTextFillColor: "#333333",
                  }
                }}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="start"><IconButton onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      onMouseUp={handleMouseUpPassword}  >
                      {showPassword ? <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15.5799 11.9999C15.5799 13.9799 13.9799 15.5799 11.9999 15.5799C10.0199 15.5799 8.41992 13.9799 8.41992 11.9999C8.41992 10.0199 10.0199 8.41992 11.9999 8.41992C13.9799 8.41992 15.5799 10.0199 15.5799 11.9999Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M12.0001 20.27C15.5301 20.27 18.8201 18.19 21.1101 14.59C22.0101 13.18 22.0101 10.81 21.1101 9.39997C18.8201 5.79997 15.5301 3.71997 12.0001 3.71997C8.47009 3.71997 5.18009 5.79997 2.89009 9.39997C1.99009 10.81 1.99009 13.18 2.89009 14.59C5.18009 18.19 8.47009 20.27 12.0001 20.27Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                        : <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14.5299 9.47004L9.46992 14.53C8.81992 13.88 8.41992 12.99 8.41992 12C8.41992 10.02 10.0199 8.42004 11.9999 8.42004C12.9899 8.42004 13.8799 8.82004 14.5299 9.47004Z" stroke="#BABABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M17.8201 5.76998C16.0701 4.44998 14.0701 3.72998 12.0001 3.72998C8.47009 3.72998 5.18009 5.80998 2.89009 9.40998C1.99009 10.82 1.99009 13.19 2.89009 14.6C3.68009 15.84 4.60009 16.91 5.60009 17.77" stroke="#BABABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M8.41992 19.5301C9.55992 20.0101 10.7699 20.2701 11.9999 20.2701C15.5299 20.2701 18.8199 18.1901 21.1099 14.5901C22.0099 13.1801 22.0099 10.8101 21.1099 9.40005C20.7799 8.88005 20.4199 8.39005 20.0499 7.93005" stroke="#BABABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M15.5099 12.7C15.2499 14.11 14.0999 15.26 12.6899 15.52" stroke="#BABABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M9.47 14.53L2 22" stroke="#BABABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M22 2L14.53 9.47" stroke="#BABABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      }
                    </IconButton>
                    </InputAdornment>,
                    startAdornment: <InputAdornment position="start"><Box display={"flex"}
                      onMouseDown={() => handleMouseDownPassword}
                      onMouseUp={() => handleMouseUpPassword}  >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 10V8C6 4.69 7 2 12 2C17 2 18 4.69 18 8V10" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M12 18.5C13.3807 18.5 14.5 17.3807 14.5 16C14.5 14.6193 13.3807 13.5 12 13.5C10.6193 13.5 9.5 14.6193 9.5 16C9.5 17.3807 10.6193 18.5 12 18.5Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M17 22H7C3 22 2 21 2 17V15C2 11 3 10 7 10H17C21 10 22 11 22 15V17C22 21 21 22 17 22Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </Box>
                    </InputAdornment>
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
              <Typography color='#333333' fontSize='14px' fontWeight='500' mb='12px' sx={{ opacity: '0.6' }}>
                Confirm Password <Box component="span" color="error.main">*</Box>
              </Typography>
              <TextField
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                variant="outlined"
                placeholder='Enter confirm password'
                type={showConfirmPassword ? 'text' : 'password'}
                required
                error={!!formErrors.confirmPassword}
                helperText={formErrors.confirmPassword}
                sx={{
                  width: '100%',
                  "& .MuiOutlinedInput-root": {
                    borderRadius: '8px',
                    borderColor: '#C7C7C7',
                    background: '#ffffff',
                    paddingRight: '0'
                  },

                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: '#333333',
                    border: '1px solid'
                  },

                  "& .MuiOutlinedInput-input": {
                    padding: '14px 16px',
                    background: '#ffffff',
                    borderRadius: '8px'
                  },
                  "&.MuiInputBase-input::placeholder": {
                    color: '#333333',
                    fontSize: '16px',
                    opacity: '0.8'
                  },
                  // Add these styles to remove autofill color
                  "& input:-webkit-autofill": {
                    WebkitBoxShadow: "0 0 0 100px #ffffff inset",
                    WebkitTextFillColor: "#333333",
                  },
                  "& input:-webkit-autofill:focus": {
                    WebkitBoxShadow: "0 0 0 100px #ffffff inset",
                    WebkitTextFillColor: "#333333",
                  }
                }}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="start"><IconButton onClick={handleClickShowConfirmPassword}
                      onMouseDown={handleMouseDownPassword}
                      onMouseUp={handleMouseUpPassword}  >
                      {showConfirmPassword ? <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15.5799 11.9999C15.5799 13.9799 13.9799 15.5799 11.9999 15.5799C10.0199 15.5799 8.41992 13.9799 8.41992 11.9999C8.41992 10.0199 10.0199 8.41992 11.9999 8.41992C13.9799 8.41992 15.5799 10.0199 15.5799 11.9999Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M12.0001 20.27C15.5301 20.27 18.8201 18.19 21.1101 14.59C22.0101 13.18 22.0101 10.81 21.1101 9.39997C18.8201 5.79997 15.5301 3.71997 12.0001 3.71997C8.47009 3.71997 5.18009 5.79997 2.89009 9.39997C1.99009 10.81 1.99009 13.18 2.89009 14.59C5.18009 18.19 8.47009 20.27 12.0001 20.27Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                        : <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14.5299 9.47004L9.46992 14.53C8.81992 13.88 8.41992 12.99 8.41992 12C8.41992 10.02 10.0199 8.42004 11.9999 8.42004C12.9899 8.42004 13.8799 8.82004 14.5299 9.47004Z" stroke="#BABABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M17.8201 5.76998C16.0701 4.44998 14.0701 3.72998 12.0001 3.72998C8.47009 3.72998 5.18009 5.80998 2.89009 9.40998C1.99009 10.82 1.99009 13.19 2.89009 14.6C3.68009 15.84 4.60009 16.91 5.60009 17.77" stroke="#BABABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M8.41992 19.5301C9.55992 20.0101 10.7699 20.2701 11.9999 20.2701C15.5299 20.2701 18.8199 18.1901 21.1099 14.5901C22.0099 13.1801 22.0099 10.8101 21.1099 9.40005C20.7799 8.88005 20.4199 8.39005 20.0499 7.93005" stroke="#BABABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M15.5099 12.7C15.2499 14.11 14.0999 15.26 12.6899 15.52" stroke="#BABABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M9.47 14.53L2 22" stroke="#BABABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M22 2L14.53 9.47" stroke="#BABABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      }
                    </IconButton>
                    </InputAdornment>,
                    startAdornment: <InputAdornment position="start"><Box display={"flex"}
                      onMouseDown={() => handleMouseDownPassword}
                      onMouseUp={() => handleMouseUpPassword}  >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 10V8C6 4.69 7 2 12 2C17 2 18 4.69 18 8V10" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M12 18.5C13.3807 18.5 14.5 17.3807 14.5 16C14.5 14.6193 13.3807 13.5 12 13.5C10.6193 13.5 9.5 14.6193 9.5 16C9.5 17.3807 10.6193 18.5 12 18.5Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M17 22H7C3 22 2 21 2 17V15C2 11 3 10 7 10H17C21 10 22 11 22 15V17C22 21 21 22 17 22Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </Box>
                    </InputAdornment>
                  },
                }}
              />
            </Grid>
          </Grid>
          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </DialogContent>
      );
    }

    return (
      <DialogContent sx={{ p: { xs: "16px", sm: "32px" } }}>
        <Box className="add-workspace-modal-pricing-section">
          <PricingSectionV2 
            onPlanSelect={getSelectedPlanId} 
            hideTabs={true} 
            isSaleLive={shouldShowSale}
            subscriptionStatus={subscriptionData?.[0]?.subscription?.status}
          />
        </Box>
      </DialogContent>
    );
  };

  const renderDialogActions = () => {
    if (currentStep === 'details') {
      return (
        <DialogActions sx={{ mb: { xs: "16px", sm: "32px" }, mr: { xs: "11px", sm: "32px" }, p: "0" }}>
          <Button onClick={handleClose} size='large' color="primary" sx={{
            border: '1px solid #E8EAF0',
            px: '22px',
            color: '#2B2B30 !important',
            textTransform: 'capitalize',
            borderRadius: '8px',
            mr: '16px',
            '&:hover': {
              border: '1px solid #E8EAF0 !important',
              boxShadow: 'unset !important'
            },
          }}>
            Cancel
          </Button>
          <Button
            variant='contained'
            size='large'
            color='primary'
            disabled={isLoading}
            onClick={handleNext}
            sx={{
              textTransform: 'capitalize',
              color: '#FFFFFF !important',
              px: '24px',
              borderRadius: '8px',
              '&:hover': {
                border: 'none !important'
              },
              minWidth: "94px",
              '&.Mui-disabled': {
                backgroundColor: '#E8EAF0',
                color: '#A3A3A3 !important'
              }
            }}
          >
            Next
          </Button>
        </DialogActions>
      );
    }

    return (
      <DialogActions sx={{ mb: { xs: "16px", sm: "32px" }, mr: { xs: "11px", sm: "32px" }, p: "0" }}>
        <Button
          onClick={() => setCurrentStep('details')}
          size='large'
          color="primary"
          sx={{
            border: '1px solid #E8EAF0',
            px: '22px',
            color: '#2B2B30 !important',
            textTransform: 'capitalize',
            borderRadius: '8px',
            mr: '16px',
            '&:hover': {
              border: '1px solid #E8EAF0 !important',
              boxShadow: 'unset !important'
            },
          }}
        >
          Back
        </Button>
      </DialogActions>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        className=''
        sx={{
          '& .MuiDialog-paper': {
            width: currentStep === 'plans' ? '100%' : '100%',
            maxWidth: currentStep === 'plans' ? 'fit-content' : '775px',
            borderRadius: '16px',
            border: '1px solid #E8E8EA',
            height: 'fit-content',
          }
        }}
      >
        <DialogTitle sx={{ m: 0, }} p={{ xs: "16px 16px 15px !important" }} borderBottom="1px solid #E8EAF0">
          <Typography color='#333333' fontSize="min(24px, calc(20px + 4 * ((100vw - 320px) / (1920 - 320))))" fontWeight='500'>Add Workspace</Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={() => ({
              position: 'absolute',
              right: { xs: "17px" },
              top: { xs: "15px" },
            })}
          >
            <ModalClose></ModalClose>
          </IconButton>
        </DialogTitle>
        {renderDialogContent()}
        {renderDialogActions()}
      </Dialog>

      {/* confirmation modal */}
      <ConfirmCardPayment
        open={PaymentModal}
        onClose={handleConfirmationClose}
        planDetails={selectedPlanDetails}
        onBuyNow={handlePlanSelect}
        isSaleLive={shouldShowSale}
      />
    </>
  );
};
export default AddWorkspace;
